import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Truck, MapPin, DollarSign, Home, Award, CheckCircle2, Phone, Briefcase, AlertTriangle } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Inject Google JobPosting schema + SEO title/description into document.head.
 * Cleanup on unmount so navigating away restores previous head state.
 */
function useCareersSeo(page) {
  useEffect(() => {
    if (!page) return undefined;

    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");

    const freightLabel = {
      dry_van: "Dry Van", reefer: "Reefer", flatbed: "Flatbed",
      step_deck: "Step Deck", power_only: "Power Only",
      hotshot: "Hotshot", multi: "Multi",
    }[page.freight_type] || page.freight_type;
    const cdlLabel = page.cdl_class === "A_or_B" ? "CDL-A or CDL-B" : `CDL-${page.cdl_class}`;

    document.title = `${cdlLabel} Truck Driver at ${page.company_name} — ${page.city}, ${page.state}`;
    const description = `${page.company_name} is hiring ${cdlLabel} drivers (${freightLabel}) out of ${page.city}, ${page.state}. Pay: ${page.pay_range}. Home time: ${page.home_time}. Apply directly on FreightPilotAI.`;
    if (metaDesc) metaDesc.setAttribute("content", description);
    else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = description;
      document.head.appendChild(m);
    }

    // Fetch server-rendered JSON-LD (keeps schema logic in one place — the API)
    let script = null;
    let cancelled = false;
    axios.get(`${API_BASE}/api/careers/pages/${page.slug}/jobposting.json`)
      .then((r) => {
        if (cancelled) return;
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-testid", "public-careers-jobposting-jsonld");
        script.text = JSON.stringify(r.data);
        document.head.appendChild(script);
      })
      .catch(() => { /* SEO is best-effort — never break page render */ });

    return () => {
      cancelled = true;
      document.title = prevTitle;
      if (metaDesc && prevDesc != null) metaDesc.setAttribute("content", prevDesc);
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [page]);
}

/**
 * PublicCareersPage — the page drivers see at /jobs/:slug.
 * Displays job info + application form. On successful submit, shows a success screen.
 */
export default function PublicCareersPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API_BASE}/api/careers/pages/${slug}`)
      .then((r) => { setPage(r.data); setLoading(false); })
      .catch((err) => { setError(err.response?.status === 404 ? "not_found" : "error"); setLoading(false); });
  }, [slug]);

  useCareersSeo(page);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5A1F]" />
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-14 h-14 text-amber-400 mx-auto mb-4" />
          <h1 className="text-3xl font-black mb-2">Careers page not found</h1>
          <p className="text-white/60 mb-6">This carrier hasn't published a hiring page — or they've taken it down.</p>
          <button onClick={() => nav("/careers-builder")} className="bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-lg px-5 py-2.5 transition-colors">
            Create your own careers page
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center">
        <p className="text-red-400">Something went wrong. Please try again.</p>
      </div>
    );
  }

  return <CareersPageDisplay page={page} />;
}

function CareersPageDisplay({ page }) {
  const [showApply, setShowApply] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const freightLabel = {
    dry_van: "Dry Van", reefer: "Reefer", flatbed: "Flatbed",
    step_deck: "Step Deck", power_only: "Power Only", hotshot: "Hotshot", multi: "Multiple types",
  }[page.freight_type] || page.freight_type;

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Minimal top strip */}
      <div className="border-b border-white/10 bg-neutral-950/80 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1 opacity-70 hover:opacity-100 transition-opacity">
            <span className="font-black text-sm tracking-tight">Freight</span>
            <span className="font-black text-sm tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-sm tracking-tight">AI</span>
            <span className="ml-2 text-[10px] text-white/40">Careers page</span>
          </Link>
          <Link to="/careers-builder" className="text-[11px] text-white/50 hover:text-white transition-colors" data-testid="public-careers-builder-link">
            Post your own job →
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #FF5A1F 0%, transparent 50%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 pt-14 pb-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-3 py-1 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-5">
            <Briefcase className="w-3 h-3" />
            Now hiring · {page.cdl_class === "A_or_B" ? "CDL-A or CDL-B" : `CDL-${page.cdl_class}`}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight mb-3" data-testid="public-careers-company">
            {page.company_name}
          </h1>
          {page.tagline && (
            <p className="text-base sm:text-lg text-[#FF5A1F] font-semibold mb-5 max-w-2xl mx-auto">
              {page.tagline}
            </p>
          )}
          <div className="flex items-center justify-center gap-2 text-white/60 text-sm mb-8">
            <MapPin className="w-4 h-4" />
            <span>Based in {page.city}, {page.state}</span>
          </div>
          <button onClick={() => setShowApply(true)}
            className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-xl px-8 py-4 text-lg transition-colors"
            data-testid="public-careers-apply-btn">
            <Briefcase className="w-5 h-5" />
            Apply now
          </button>
        </div>
      </section>

      {/* Job details grid */}
      <section className="max-w-4xl mx-auto px-6 pb-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={<DollarSign className="w-5 h-5" />} label="Pay" value={page.pay_range} />
          <Stat icon={<Home className="w-5 h-5" />} label="Home time" value={page.home_time} />
          <Stat icon={<Truck className="w-5 h-5" />} label="Freight" value={freightLabel} />
          <Stat icon={<Award className="w-5 h-5" />} label="Min. experience" value={`${page.min_experience_years}+ years`} />
        </div>
      </section>

      {/* Benefits */}
      {page.benefits && page.benefits.length > 0 && (
        <section className="max-w-4xl mx-auto px-6 pb-8">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8">
            <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-4">
              Why drive for us
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {page.benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <span className="text-white/90 leading-snug">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact + secondary CTA */}
      <section className="max-w-4xl mx-auto px-6 pb-12">
        <div className="rounded-2xl border-2 border-[#FF5A1F]/40 bg-gradient-to-br from-[#FF5A1F]/20 to-transparent p-6 sm:p-8 text-center">
          <h3 className="text-2xl font-black mb-2 tracking-tight">Ready to drive with us?</h3>
          {page.contact_phone && (
            <div className="flex items-center justify-center gap-2 text-white/70 text-sm mb-4">
              <Phone className="w-4 h-4" />
              <a href={`tel:${page.contact_phone}`} className="hover:text-white" data-testid="public-careers-phone">
                Call {formatPhone(page.contact_phone)}
              </a>
              <span className="text-white/30">·</span>
              <span>or apply below</span>
            </div>
          )}
          <button onClick={() => setShowApply(true)}
            className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-lg px-6 py-3 transition-colors"
            data-testid="public-careers-apply-btn-2">
            Start your application
            <Briefcase className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer credit */}
      <footer className="border-t border-white/10 py-6">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-2">
          <div className="text-[11px] text-white/40">
            Careers page powered by <Link to="/" className="text-white/70 hover:text-white font-semibold">FreightPilotAI</Link> — free for every small carrier
          </div>
          <div className="text-[11px] text-white/30">
            Are you {page.company_name}? <Link to={`/careers-edit?slug=${page.slug}`} className="text-white/50 hover:text-[#FF5A1F] underline transition-colors" data-testid="public-careers-edit-link">Edit this page →</Link>
          </div>
        </div>
      </footer>

      {/* Apply modal */}
      {showApply && !submitted && (
        <ApplyModal slug={page.slug} company={page.company_name} onClose={() => setShowApply(false)} onSubmitted={() => setSubmitted(true)} />
      )}
      {submitted && (
        <SubmittedModal company={page.company_name} onClose={() => { setSubmitted(false); setShowApply(false); }} />
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="w-9 h-9 rounded-lg bg-[#FF5A1F]/20 text-[#FF5A1F] flex items-center justify-center mb-2">
        {icon}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-0.5">{label}</div>
      <div className="text-white font-semibold text-sm leading-tight">{value}</div>
    </div>
  );
}

function formatPhone(phone) {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === "1") return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return phone;
}

/* ─────────────────── Apply modal ─────────────────── */
function ApplyModal({ slug, company, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    applicant_name: "", applicant_email: "", applicant_phone: "",
    years_experience: 2, cdl_class: "A", current_location: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/api/careers/apply`, {
        slug,
        ...form,
        years_experience: parseInt(form.years_experience, 10) || 0,
      });
      onSubmitted();
    } catch (err) {
      setError(err.response?.data?.detail || "Application failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="bg-neutral-900 border-2 border-[#FF5A1F]/40 rounded-2xl max-w-lg w-full my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="p-6 sm:p-8 space-y-4" data-testid="public-careers-apply-form">
          <div className="mb-2">
            <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1">Applying to</div>
            <div className="text-xl font-black">{company}</div>
          </div>

          <Field label="Full name" value={form.applicant_name} onChange={update("applicant_name")} testid="apply-name" required />
          <Field label="Email" type="email" value={form.applicant_email} onChange={update("applicant_email")} testid="apply-email" required />
          <Field label="Phone" type="tel" value={form.applicant_phone} onChange={update("applicant_phone")} placeholder="(605) 555-1234" testid="apply-phone" required />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Years OTR experience</Label>
              <input type="number" value={form.years_experience} onChange={update("years_experience")} min="0" max="60"
                className="w-full bg-neutral-950 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white outline-none transition-colors"
                data-testid="apply-experience" />
            </div>
            <div>
              <Label>CDL class</Label>
              <select value={form.cdl_class} onChange={update("cdl_class")}
                className="w-full bg-neutral-950 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white outline-none transition-colors"
                data-testid="apply-cdl">
                <option value="A">Class A</option>
                <option value="B">Class B</option>
              </select>
            </div>
          </div>

          <Field label="Current location (city, state)" value={form.current_location} onChange={update("current_location")} placeholder="Wheaton, IL" testid="apply-location" />

          <div>
            <Label>Anything else you want them to know (optional)</Label>
            <textarea value={form.notes} onChange={update("notes")} rows={3}
              placeholder="e.g., When you can start, references, hazmat/tanker endorsements..."
              className="w-full bg-neutral-950 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white placeholder-white/30 outline-none transition-colors resize-vertical"
              data-testid="apply-notes" />
          </div>

          {error && <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{error}</div>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 bg-white/5 hover:bg-white/10 text-white/70 font-semibold rounded-lg px-4 py-2.5 transition-colors"
              data-testid="apply-cancel">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-[2] bg-[#FF5A1F] hover:bg-[#FF7038] disabled:bg-white/10 disabled:text-white/40 text-black font-bold rounded-lg px-4 py-2.5 transition-colors flex items-center justify-center gap-2"
              data-testid="apply-submit">
              {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>) : "Submit application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SubmittedModal({ company, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-neutral-900 border-2 border-emerald-500/60 rounded-2xl max-w-md w-full p-8 text-center" onClick={(e) => e.stopPropagation()} data-testid="apply-submitted">
        <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
        <h2 className="text-2xl font-black mb-2 text-emerald-100">Application sent! 🎉</h2>
        <p className="text-white/70 mb-6">
          {company} received your info. If you're a match, they'll reach out via phone or email — usually within 48 hours. Good luck!
        </p>
        <button onClick={onClose} className="bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-lg px-5 py-2.5 transition-colors">
          Close
        </button>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5">{children}</div>;
}

function Field({ label, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <div>
      <Label>{label}</Label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required}
        className="w-full bg-neutral-950 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white placeholder-white/30 outline-none transition-colors"
        data-testid={testid}
      />
    </div>
  );
}
